import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
     Box,
     Typography,
     Paper,
     Grid,
     Card,
     CardContent,
     CardActions,
     Button,
     Chip,
     Dialog,
     DialogTitle,
     DialogContent,
     DialogActions,
     TextField,
     MenuItem,
     FormControl,
     InputLabel,
     Select,
     IconButton,
     Snackbar,
     Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const CATEGORIES = [
     'Pre-Launch',
     'Launch',
     'Site Visit',
     'Follow-up',
     'Post-Launch'
];

const WhatsAppTemplates = () => {
     const [templates, setTemplates] = useState([]);
     const [loading, setLoading] = useState(true);
     const [openDialog, setOpenDialog] = useState(false);
     const [openSendDialog, setOpenSendDialog] = useState(false);
     const [currentTemplate, setCurrentTemplate] = useState({
          title: '',
          category: 'Pre-Launch',
          content: '',
          variables: []
     });
     const [isEditing, setIsEditing] = useState(false);
     const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

     // For sending
     const [selectedTemplate, setSelectedTemplate] = useState(null);
     const [clientPhone, setClientPhone] = useState('');
     const [clientName, setClientName] = useState('');
     const [previewMessage, setPreviewMessage] = useState('');

     useEffect(() => {
          fetchTemplates();
     }, []);

     const fetchTemplates = async () => {
          try {
               const token = localStorage.getItem('token');
               const response = await axios.get('/api/whatsapp-templates', {
                    headers: { Authorization: `Bearer ${token}` }
               });
               setTemplates(response.data);
               setLoading(false);
          } catch (error) {
               console.error('Error fetching templates:', error);
               setLoading(false);
          }
     };

     const handleOpenDialog = (template = null) => {
          if (template) {
               setCurrentTemplate({
                    ...template,
                    variables: typeof template.variables === 'string' ? JSON.parse(template.variables) : template.variables
               });
               setIsEditing(true);
          } else {
               setCurrentTemplate({
                    title: '',
                    category: 'Pre-Launch',
                    content: '',
                    variables: []
               });
               setIsEditing(false);
          }
          setOpenDialog(true);
     };

     const handleSave = async () => {
          try {
               const token = localStorage.getItem('token');

               // Auto-detect variables from content ({{variable}})
               const regex = /{{([^}]+)}}/g;
               const foundVariables = [];
               let match;
               while ((match = regex.exec(currentTemplate.content)) !== null) {
                    if (!foundVariables.includes(match[1])) {
                         foundVariables.push(match[1]);
                    }
               }
               const templateToSave = { ...currentTemplate, variables: foundVariables };

               if (isEditing) {
                    await axios.put(`/api/whatsapp-templates/${currentTemplate.id}`, templateToSave, {
                         headers: { Authorization: `Bearer ${token}` }
                    });
                    setSnackbar({ open: true, message: 'Template updated successfully', severity: 'success' });
               } else {
                    await axios.post('/api/whatsapp-templates', templateToSave, {
                         headers: { Authorization: `Bearer ${token}` }
                    });
                    setSnackbar({ open: true, message: 'Template created successfully', severity: 'success' });
               }
               setOpenDialog(false);
               fetchTemplates();
          } catch (error) {
               console.error('Error saving template:', error);
               setSnackbar({ open: true, message: 'Failed to save template', severity: 'error' });
          }
     };

     const handleDelete = async (id) => {
          if (!window.confirm('Are you sure you want to delete this template?')) return;
          try {
               const token = localStorage.getItem('token');
               await axios.delete(`/api/whatsapp-templates/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
               });
               setSnackbar({ open: true, message: 'Template deleted', severity: 'success' });
               fetchTemplates();
          } catch (error) {
               console.error('Error deleting template:', error);
          }
     };

     const handleUseTemplate = (template) => {
          setSelectedTemplate(template);

          // Parse variables if needed
          const vars = typeof template.variables === 'string' ? JSON.parse(template.variables) : template.variables;

          // Pre-fill preview with default replacement (ignoring variables for now, just raw text or simple regex)
          let msg = template.content;
          setPreviewMessage(msg);
          setOpenSendDialog(true);
     };

     const updatePreview = () => {
          if (!selectedTemplate) return;
          let msg = selectedTemplate.content;
          msg = msg.replace(/{{name}}/g, clientName || 'Client');
          // We can add more variable replacements here like project name, pricing etc.
          setPreviewMessage(msg);
     };

     useEffect(() => {
          updatePreview();
     }, [clientName, selectedTemplate]);

     const handleSendWhatsApp = () => {
          if (!clientPhone) {
               alert('Please enter a phone number');
               return;
          }

          const encodedMessage = encodeURIComponent(previewMessage);
          // Remove non-digit chars from phone
          const cleanPhone = clientPhone.replace(/\D/g, '');
          const url = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;

          window.open(url, '_blank');
          setOpenSendDialog(false);
     };

     const handleCopy = (content) => {
          navigator.clipboard.writeText(content);
          setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'info' });
     };

     // Group templates by category
     const groupedTemplates = CATEGORIES.reduce((acc, category) => {
          acc[category] = templates.filter(t => t.category === category);
          return acc;
     }, {});

     return (
          <Box sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                         WhatsApp 2026 Strategy
                    </Typography>
                    <Button
                         variant="contained"
                         startIcon={<AddIcon />}
                         onClick={() => handleOpenDialog()}
                         sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                    >
                         New Script
                    </Button>
               </Box>

               {CATEGORIES.map(category => (
                    groupedTemplates[category]?.length > 0 && (
                         <Box key={category} sx={{ mb: 4 }}>
                              <Typography variant="h6" sx={{ mb: 2, color: '#555', borderBottom: '2px solid #eee', pb: 1 }}>
                                   {category}
                              </Typography>
                              <Grid container spacing={3}>
                                   {groupedTemplates[category].map(template => (
                                        <Grid item xs={12} md={6} lg={4} key={template.id}>
                                             <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                                                  <CardContent sx={{ flexGrow: 1 }}>
                                                       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                 {template.title}
                                                            </Typography>
                                                            <Chip label={template.category} size="small" color="primary" variant="outlined" />
                                                       </Box>
                                                       <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, maxHeight: 150, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                                            {template.content}
                                                       </Paper>
                                                  </CardContent>
                                                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                                       <Box>
                                                            <IconButton size="small" onClick={() => handleOpenDialog(template)}><EditIcon /></IconButton>
                                                            <IconButton size="small" color="error" onClick={() => handleDelete(template.id)}><DeleteIcon /></IconButton>
                                                       </Box>
                                                       <Box>
                                                            <IconButton size="small" onClick={() => handleCopy(template.content)} sx={{ mr: 1 }}><ContentCopyIcon /></IconButton>
                                                            <Button
                                                                 variant="contained"
                                                                 size="small"
                                                                 startIcon={<WhatsAppIcon />}
                                                                 onClick={() => handleUseTemplate(template)}
                                                                 sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                                                            >
                                                                 Use
                                                            </Button>
                                                       </Box>
                                                  </CardActions>
                                             </Card>
                                        </Grid>
                                   ))}
                              </Grid>
                         </Box>
                    )
               ))}

               {/* Create/Edit Dialog */}
               <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>{isEditing ? 'Edit Script' : 'New WhatsApp Script'}</DialogTitle>
                    <DialogContent>
                         <TextField
                              autoFocus
                              margin="dense"
                              label="Script Title"
                              fullWidth
                              value={currentTemplate.title}
                              onChange={(e) => setCurrentTemplate({ ...currentTemplate, title: e.target.value })}
                              sx={{ mb: 2 }}
                         />
                         <FormControl fullWidth sx={{ mb: 2 }}>
                              <InputLabel>Category</InputLabel>
                              <Select
                                   value={currentTemplate.category}
                                   label="Category"
                                   onChange={(e) => setCurrentTemplate({ ...currentTemplate, category: e.target.value })}
                              >
                                   {CATEGORIES.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                              </Select>
                         </FormControl>
                         <TextField
                              margin="dense"
                              label="Message Content"
                              fullWidth
                              multiline
                              rows={6}
                              value={currentTemplate.content}
                              onChange={(e) => setCurrentTemplate({ ...currentTemplate, content: e.target.value })}
                              helperText="Use {{name}} for client name, {{amount}} for money, etc."
                         />
                    </DialogContent>
                    <DialogActions>
                         <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                         <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
                    </DialogActions>
               </Dialog>

               {/* Send Dialog */}
               <Dialog open={openSendDialog} onClose={() => setOpenSendDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Send Message</DialogTitle>
                    <DialogContent>
                         <Typography variant="body2" color="textSecondary" paragraph>
                              Enter client details to preview and send.
                         </Typography>
                         <TextField
                              label="Client Name"
                              fullWidth
                              sx={{ mb: 2, mt: 1 }}
                              value={clientName}
                              onChange={(e) => setClientName(e.target.value)}
                         />
                         <TextField
                              label="Phone Number (with Code)"
                              fullWidth
                              sx={{ mb: 2 }}
                              value={clientPhone}
                              onChange={(e) => setClientPhone(e.target.value)}
                              placeholder="e.g. 919876543210"
                         />
                         <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview:</Typography>
                         <Paper sx={{ p: 2, bgcolor: '#e1f5fe', borderRadius: 2, whiteSpace: 'pre-wrap' }}>
                              {previewMessage}
                         </Paper>
                    </DialogContent>
                    <DialogActions>
                         <Button onClick={() => setOpenSendDialog(false)}>Cancel</Button>
                         <Button
                              onClick={handleSendWhatsApp}
                              variant="contained"
                              startIcon={<WhatsAppIcon />}
                              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                         >
                              Open WhatsApp
                         </Button>
                    </DialogActions>
               </Dialog>

               <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                         {snackbar.message}
                    </Alert>
               </Snackbar>
          </Box>
     );
};

export default WhatsAppTemplates;
